import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { PageMeta } from "@/components/site/PageMeta";
import { AssessmentShell, ScaleQuestion } from "@/components/built-index/AssessmentShell";
import { BUILT_INDEX_ROUTES, LOVIN_DIMENSIONS, type LovinKey } from "@/content/built-index";
import { readSession, submitBuiltIndex, writeSession } from "@/lib/built-index-session";
import { trackBuiltIndexStep } from "@/lib/built-index-analytics";

const BuiltIndexListening = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState<Partial<Record<LovinKey, number>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const s = readSession();
    if (!s.id) {
      navigate(BUILT_INDEX_ROUTES.start, { replace: true });
      return;
    }
    setAnswers(s.lovin);
  }, [navigate]);

  const set = (key: LovinKey, value: number) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    writeSession({ lovin: next });
  };

  const answered = LOVIN_DIMENSIONS.filter((d) => typeof answers[d.key] === "number").length;

  const finish = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const state = writeSession({ lovin: answers });
      await submitBuiltIndex(state);
      trackBuiltIndexStep("assessment_lovin", {
        sessionId: state.id,
        email: state.email,
        metadata: { answered },
      });
      navigate(`${BUILT_INDEX_ROUTES.results}?id=${state.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setBusy(false);
    }
  };

  return (
    <>
      <PageMeta
        title="The LOVIN Listening Scale · The Built Index"
        description="Measure the listening skill that decides whether your team tells you the truth."
        path="/built-index/listening"
        noindex
      />
      <AssessmentShell
        step={4}
        title="The LOVIN Listening Scale"
        intro="Listening is the skill that decides whether your team brings you problems early or late. Rate each dimension from 1 to 10."
        footer={
          <div className="space-y-4">
            {error && (
              <p role="alert" className="text-sm text-destructive-foreground bg-destructive/20 border border-destructive/40 px-4 py-3">
                {error}
              </p>
            )}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <span className="text-silver text-xs uppercase tracking-[0.2em]">
                {answered} of {LOVIN_DIMENSIONS.length} answered
              </span>
              <button
                type="button"
                disabled={answered !== LOVIN_DIMENSIONS.length || busy}
                onClick={finish}
                className="inline-flex items-center gap-2 bg-orange text-primary-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold hover:brightness-110 transition disabled:opacity-50"
              >
                {busy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Building your index…
                  </>
                ) : (
                  <>
                    See My Built Index <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        }
      >
        {LOVIN_DIMENSIONS.map((d) => (
          <ScaleQuestion
            key={d.key}
            id={d.key}
            label={`${d.letter} · ${d.name}`}
            prompt={d.prompt}
            value={answers[d.key]}
            onChange={(v) => set(d.key, v)}
          />
        ))}
      </AssessmentShell>
    </>
  );
};

export default BuiltIndexListening;
